"use client"

import type { TranslateFn } from '@open-mercato/shared/lib/i18n/context'
import type { ColumnDef } from '@tanstack/react-table'
import type { FilterDef } from '@open-mercato/ui/backend/FilterBar'
import { Badge } from '@open-mercato/ui/primitives/badge'
import { DISPATCH_STATUSES, type DispatchStatus } from '../lib/lookups'

export type DispatchListItem = {
  id: string
  legId: string
  vehicleId: string
  driverId: string
  vehiclePlate: string | null
  driverName: string | null
  trailerId: string | null
  status: DispatchStatus
  plannedPickupAt: string | null
  plannedDeliveryAt: string | null
  actualDepartureAt: string | null
  actualPickupAt: string | null
  actualDeliveryAt: string | null
  completedAt: string | null
  dispatcherNotes: string | null
  version: number
  createdAt: string
  updatedAt: string
}

export type DispatchesListResponse = {
  items: DispatchListItem[]
  page: number
  pageSize: number
  total: number
  totalPages: number
}

function getDispatchStatusVariant(status: DispatchStatus): 'default' | 'secondary' | 'destructive' | 'muted' | 'outline' {
  switch (status) {
    case 'ASSIGNED': return 'secondary'
    case 'EN_ROUTE_PICKUP': return 'default'
    case 'AT_PICKUP': return 'default'
    case 'LOADING': return 'default'
    case 'IN_TRANSIT': return 'default'
    case 'AT_DELIVERY': return 'default'
    case 'UNLOADING': return 'default'
    case 'COMPLETED': return 'outline'
    case 'CANCELLED': return 'muted'
    default: return 'secondary'
  }
}

function formatDateTime(value: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

export function buildDispatchesColumns(t: TranslateFn): ColumnDef<DispatchListItem>[] {
  return [
    {
      id: 'status',
      accessorKey: 'status',
      header: () => t('dispatch.table.columns.status', 'Status'),
      cell: ({ row }) => {
        const status = row.original.status as DispatchStatus
        const label = t(`dispatch.lookups.dispatchStatus.${status}`, status)
        return <Badge variant={getDispatchStatusVariant(status)}>{label}</Badge>
      },
    },
    {
      id: 'vehicleId',
      accessorKey: 'vehicleId',
      header: () => t('dispatch.table.columns.vehicleId', 'Vehicle'),
      cell: ({ row }) => row.original.vehiclePlate ?? row.original.vehicleId,
    },
    {
      id: 'driverId',
      accessorKey: 'driverId',
      header: () => t('dispatch.table.columns.driverId', 'Driver'),
      cell: ({ row }) => row.original.driverName ?? row.original.driverId,
    },
    {
      id: 'plannedPickupAt',
      accessorKey: 'plannedPickupAt',
      header: () => t('dispatch.table.columns.plannedPickupAt', 'Planned pickup'),
      cell: ({ row }) => formatDateTime(row.original.plannedPickupAt),
    },
    {
      id: 'plannedDeliveryAt',
      accessorKey: 'plannedDeliveryAt',
      header: () => t('dispatch.table.columns.plannedDeliveryAt', 'Planned delivery'),
      cell: ({ row }) => formatDateTime(row.original.plannedDeliveryAt),
    },
  ]
}

export function buildDispatchesFilters(t: TranslateFn): FilterDef[] {
  return [
    {
      id: 'status',
      label: t('dispatch.table.filters.status', 'Status'),
      type: 'select',
      placeholder: t('dispatch.table.filters.statusPlaceholder', 'Filter by status'),
      options: DISPATCH_STATUSES.map((s) => ({
        value: s,
        label: t(`dispatch.lookups.dispatchStatus.${s}`, s),
      })),
    },
  ]
}
